import React, { useCallback, useState } from "react";
import { useIntl } from "react-intl";
import { BorderedBox } from "@fhx/ui";
import classNames from "classnames";
import ReactImageCarousel, { ModalGateway, Modal as ReactImageModal } from "react-images";
import styles from "./DownloadEntryWindow.module.scss";
import DownloadEntryFooter from "./components/DownloadEntryFooter/DownloadEntryFooter";
import { IDownloadableContent } from "../../../../../../interfaces";
import { DefaultWindow } from "../../../../../windows";

interface ICarouselEntry {
  source: {
    regular: string;
    thumbnail?: string;
    fullscreen?: string;
  };
}

export interface IProps {
  entry: IDownloadableContent;
  className?: string;
  style?: React.CSSProperties;
}

export default function DownloadEntryWindow({ entry, className, style }: IProps): React.ReactElement {
  const { locale } = useIntl();
  const { id, icon, screenshots } = entry;
  const i18nName = entry.i18nName as unknown as Record<string, string>;
  const i18nDescription = entry.i18nDescription as unknown as Record<string, React.ReactNode>;
  const [isModalOpen, setIsModalOpen] = useState<boolean>(false);
  const [currentIndex, setCurrentIndex] = useState<number>(0);

  const renderScreenshots = useCallback((): React.ReactNode => {
    if (!screenshots) return <></>;

    const screenshotEntries: Array<ICarouselEntry> | undefined = screenshots.map((url) => {
      const thumbnailUrl = url; // TODO
      return {
        source: {
          regular: url,
          thumbnail: thumbnailUrl,
          fullscreen: url,
        },
      };
    });

    return (
      <div className={styles.screenshots}>
        <ModalGateway>
          {isModalOpen && (
            <ReactImageModal onClose={(): void => setIsModalOpen(false)}>
              <ReactImageCarousel views={screenshotEntries} currentIndex={currentIndex} />
            </ReactImageModal>
          )}
        </ModalGateway>
        {screenshotEntries.map((screenshotEntry, index): React.ReactNode => {
          const { source } = screenshotEntry;
          /* eslint-disable react/no-array-index-key */
          return (
            <a
              key={`${id}_${index}`}
              href={`#screenshot:${id}_${index}`}
              className={styles.entry}
              onClick={(): void => {
                setCurrentIndex(index);
                setIsModalOpen(true);
              }}
            >
              <BorderedBox type="TOOLTIP" isGapless className={styles.screenshotEntryBox}>
                <img src={source.thumbnail || source.regular} className={styles.screenshotThumbnail} alt="" />
              </BorderedBox>
            </a>
          );
        })}
      </div>
    );
  }, [currentIndex, id, isModalOpen, screenshots]);

  const header = (
    <>
      {icon && <img src={icon} alt="" />}
      {i18nName[locale] || i18nName.en}
    </>
  );
  return (
    <DefaultWindow
      header={header}
      footer={<DownloadEntryFooter entry={entry} />}
      className={classNames(styles.window, className)}
      contentClassName={styles.content}
      style={style}
    >
      {i18nDescription && <div className={styles.description}>{i18nDescription[locale] || i18nDescription.en}</div>}
      {renderScreenshots()}
    </DefaultWindow>
  );
}
